/**
 * Login Lockout Manager — Johan's support surface for failed-login timers.
 *
 * Source of truth is public.users (failed_login_attempts + lockout_until).
 * No new financial table. Reset only clears those two lockout columns via
 * POST /api/admin/security/account-lockouts.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';

interface LockedAccount {
  id: number;
  username: string | null;
  email: string;
  full_name: string | null;
  failed_login_attempts: number;
  lockout_until: string | null;
  last_known_ip: string | null;
  last_failed_login_at: string | null;
  last_failed_login_ip: string | null;
  trusted_ips: string[] | null;
}

function isLockoutActive(lockoutUntil: string | null, nowMs: number): boolean {
  return Boolean(lockoutUntil) && new Date(lockoutUntil as string).getTime() > nowMs;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

function formatRemaining(lockoutUntil: string | null, nowMs: number): string {
  if (!lockoutUntil) return '—';
  const ms = new Date(lockoutUntil).getTime() - nowMs;
  if (ms <= 0) return 'expired';
  const totalSec = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function lockoutStatus(account: LockedAccount, nowMs: number): 'LOCKED' | 'WARN' | 'CLEAR' {
  if (isLockoutActive(account.lockout_until, nowMs)) return 'LOCKED';
  if ((account.failed_login_attempts || 0) > 0) return 'WARN';
  return 'CLEAR';
}

const LoginLockoutManager: React.FC = () => {
  const [accounts, setAccounts] = useState<LockedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [lookupId, setLookupId] = useState('');
  const [filter, setFilter] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    ...getSessionHeaders(),
  });

  const fetchLockouts = useCallback(async () => {
    setError(null);
    try {
      const response = await fetch('/api/admin/security/account-lockouts', {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to load lockouts (${response.status})`);
      }
      setAccounts(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load lockouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLockouts();
  }, [fetchLockouts]);

  const visibleAccounts = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((account) => {
      return (
        String(account.id).includes(q) ||
        (account.username || '').toLowerCase().includes(q) ||
        (account.email || '').toLowerCase().includes(q) ||
        (account.full_name || '').toLowerCase().includes(q)
      );
    });
  }, [accounts, filter]);

  const activeCount = accounts.filter((account) => isLockoutActive(account.lockout_until, nowMs)).length;
  const warnCount = accounts.filter((account) => !isLockoutActive(account.lockout_until, nowMs) && (account.failed_login_attempts || 0) > 0).length;

  const resetLockout = async (userId: number, label: string) => {
    const confirmed = window.confirm(
      `Reset login lockout for ${label} (ID ${userId})?\n\nThis clears the countdown timer and failed-attempt counter so they can sign in immediately.\nIt does NOT change password, balances, shares, or KYC.`
    );
    if (!confirmed) return;

    setActionLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/security/account-lockouts', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          userId,
          reason: 'Admin support reset after member lockout enquiry',
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to reset lockout (${response.status})`);
      }
      setSuccessMessage(`Lockout reset for ${label} (ID ${userId}). They can sign in now.`);
      setAccounts((prev) => prev.filter((account) => account.id !== userId));
    } catch (err: any) {
      setError(err.message || 'Failed to reset lockout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLookup = async (event: React.FormEvent) => {
    event.preventDefault();
    const userId = Number(lookupId.trim());
    if (!Number.isInteger(userId) || userId <= 0) {
      setError('Enter a valid numeric user ID (example: 7710).');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/security/account-lockouts?userId=${userId}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Lookup failed (${response.status})`);
      }
      const found: LockedAccount | undefined = (data.data || [])[0];
      if (!found) {
        setError(`No user found for ID ${userId}.`);
        return;
      }
      setAccounts((prev) => {
        const without = prev.filter((account) => account.id !== found.id);
        return [found, ...without];
      });
      setFilter(String(found.id));
      setSuccessMessage(`Loaded ${found.full_name || found.username || found.email} (ID ${found.id}).`);
    } catch (err: any) {
      setError(err.message || 'Lookup failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetById = async (event: React.FormEvent) => {
    event.preventDefault();
    const userId = Number(lookupId.trim());
    if (!Number.isInteger(userId) || userId <= 0) {
      setError('Enter a valid numeric user ID (example: 7710).');
      return;
    }
    const existing = accounts.find((account) => account.id === userId);
    await resetLockout(userId, existing?.username || existing?.email || `user ${userId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-yellow-500">Login Lockouts</h1>
        <p className="text-gray-300 mt-1">
          Failed-password timers (5 fails = 15 min, 10 = 1 hour, 15 = 24 hours).
          Reset clears the countdown and attempt counter only. Not a suspension, not a financial change.
        </p>
      </div>

      {successMessage && (
        <div className="mb-4 p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-300">
          {successMessage}
          <button onClick={() => setSuccessMessage('')} className="float-right">뿯½</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-300">
          {error}
          <button onClick={() => setError(null)} className="float-right">뿯½</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-3xl font-bold text-purple-400">{activeCount}</div>
          <div className="text-sm text-gray-300">Active lockout timers</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-3xl font-bold text-yellow-400">{warnCount}</div>
          <div className="text-sm text-gray-300">Expired timer, leftover attempts</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="text-3xl font-bold text-white">{accounts.length}</div>
          <div className="text-sm text-gray-300">Rows in current view</div>
        </div>
      </div>

      <form onSubmit={handleLookup} className="bg-gray-800 rounded-lg p-4 mb-4 border border-gray-700">
        <h2 className="text-lg font-semibold text-yellow-500 mb-3">Lookup / reset by user ID</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            inputMode="numeric"
            placeholder="User ID (e.g. 7710)"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
          />
          <button
            type="submit"
            disabled={actionLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
          >
            Lookup ID
          </button>
          <button
            type="button"
            onClick={handleResetById}
            disabled={actionLoading}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
          >
            Reset lockout
          </button>
          <button
            type="button"
            onClick={() => { setFilter(''); setLookupId(''); fetchLockouts(); }}
            disabled={actionLoading}
            className="bg-gray-600 hover:bg-gray-500 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
          >
            Refresh list
          </button>
        </div>
      </form>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Filter table by ID, name, username, or email"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
        />
      </div>

      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Username</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Email</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Attempts</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Locked until</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Time left</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Last failed</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Failed IP</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Reset</th>
            </tr>
          </thead>
          <tbody>
            {visibleAccounts.map((account) => {
              const status = lockoutStatus(account, nowMs);
              return (
                <tr key={account.id} className="border-t border-gray-700">
                  <td className="px-4 py-3 font-mono text-yellow-400">{account.id}</td>
                  <td className="px-4 py-3 text-white">{account.full_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-300">{account.username || '—'}</td>
                  <td className="px-4 py-3 text-gray-300 text-sm">{account.email}</td>
                  <td className="px-4 py-3 font-mono text-orange-400">{account.failed_login_attempts || 0}</td>
                  <td className="px-4 py-3 text-sm text-white">{formatDate(account.lockout_until)}</td>
                  <td className="px-4 py-3 font-mono text-purple-300">{formatRemaining(account.lockout_until, nowMs)}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{formatDate(account.last_failed_login_at)}</td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-400">{account.last_failed_login_ip || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      status === 'LOCKED' ? 'bg-purple-900 text-purple-300' :
                      status === 'WARN' ? 'bg-yellow-900/40 text-yellow-300' :
                      'bg-gray-600 text-gray-200'
                    }`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => resetLockout(account.id, account.username || account.email)}
                      disabled={actionLoading}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded disabled:opacity-50"
                    >
                      Reset lockout
                    </button>
                  </td>
                </tr>
              );
            })}
            {visibleAccounts.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                  No lockout-timer accounts in this view. Lookup an ID if the member enquired after the timer expired.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LoginLockoutManager;
